import * as z from 'zod';
import { childIdSchema, collectionIdSchema, familyIdSchema } from '../primitives/id.js';
import { languageSchema, MAX_FOREIGN_LANGUAGES } from '../primitives/language.js';
import { localeSchema } from '../primitives/locale.js';
import { durationSecondsSchema, instantSchema } from '../primitives/time.js';
import { scheduleSchema, screenTimeBudgetSchema } from './screen-time.js';
/**
 * Broad age band rather than a birth date.
 *
 * The product needs to know how simple to make the interface, not who the child
 * is. Storing less is the point (SPEC.md §2.4, §62).
 */
export const ageBandSchema = z.enum(['TODDLER', 'PRESCHOOL', 'EARLY_SCHOOL']);
export const childSchema = z.object({
    id: childIdSchema,
    familyId: familyIdSchema,
    /** A first name or nickname. Never a full legal name. */
    name: z.string().min(1).max(40),
    ageBand: ageBandSchema,
    /** Identifier for a bundled illustration, never an uploaded photograph. */
    avatarKey: z.string().min(1).max(40),
    createdAt: instantSchema,
});
/**
 * What the child's own device needs in order to greet them.
 *
 * The Kids App shows a name and an avatar in the corner of the home screen, and
 * the configuration snapshot previously carried neither — only an identifier,
 * which is not something a three-year-old recognises. Deliberately smaller than
 * `Child`: a device gets what it draws and nothing more.
 */
export const childSummarySchema = z.object({
    id: childIdSchema,
    name: z.string().min(1).max(40),
    avatarKey: z.string().min(1).max(40),
});
/**
 * The range a parent can set the too-close threshold within.
 *
 * Fifteen is nearer than anyone holds a tablet to watch; sixty is further than
 * a toddler with one in her lap can sit. Outside that, the estimate is not
 * something to act on.
 */
export const DISTANCE_WARNING_CENTIMETRES = { min: 15, max: 60 };
export const childProfileSchema = z.object({
    childId: childIdSchema,
    locale: localeSchema,
    /**
     * The language this child understands best.
     *
     * What a video is downloaded in when the parent does not say otherwise. A
     * two-year-old follows a story in the language she has and loses the thread
     * in one she does not, so this is the default rather than the video's
     * original audio.
     */
    nativeLanguage: languageSchema,
    /**
     * Languages a parent may choose to practise, offered beside the native one
     * when importing. Never contains `nativeLanguage`; the server drops it.
     */
    foreignLanguages: z.array(languageSchema).max(MAX_FOREIGN_LANGUAGES),
    budget: screenTimeBudgetSchema,
    schedule: scheduleSchema,
    /**
     * Collections are defined per family and switched on per child, which is what
     * lets a parent build once and open different parts of it to each child
     * (SPEC.md §33.1).
     */
    enabledCollectionIds: z.array(collectionIdSchema),
    movementBreaksEnabled: z.boolean(),
    /**
     * How often, how long, and how many an hour.
     *
     * `SPEC.md` §24.2 says "after a configured stretch", and the cap is §24.1's
     * requirement rather than a nicety: the cost of interrupting a child who was
     * watching happily is a crying child and a deleted application, and the cost
     * of a missed break is nothing at all.
     */
    movementBreak: z.object({
        everySeconds: durationSecondsSchema,
        lastsSeconds: durationSecondsSchema,
        maxPerHour: z.int().nonnegative().max(12),
    }),
    /**
     * The parent's consent to the camera, and nothing is derived without it.
     *
     * §20–§22 put active viewing behind native camera processing: signals derived
     * on the device, no frame leaving it, nothing persisted, no identification.
     * The camera adapter is not built — the device reports "cannot see" and the
     * decision layer treats that as unknown — but the consent lives here rather
     * than being added later, because a switch bolted onto a shipped camera is a
     * switch somebody has already been watched without.
     */
    activeViewingEnabled: z.boolean(),
    /**
     * §25: *exact thresholds must not be hardcoded globally.*
     *
     * A four-year-old sits still through a story in a way a two-year-old does
     * not, and the number that separates attention from slump is not the same for
     * both. It belongs to the child.
     */
    activeViewing: z.object({
        /** Below this, movement counts as stillness. Zero to one. */
        passiveMotionScore: z.number().min(0).max(1),
        /** Stillness for longer than this is what §20 calls prolonged. */
        passiveAfterSeconds: durationSecondsSchema,
        /** Recent enough interaction to count as movement without a camera. */
        interactionCountsForSeconds: durationSecondsSchema,
    }),
    /**
     * Whether the tablet stops a video while the child is too close (§23).
     *
     * Uses the front camera, on the device, under every rule of §22: what crosses
     * into the application is whether a face is there and roughly how far, and
     * nothing else, and nothing is kept. Its own switch rather than part of
     * `activeViewingEnabled`, because a parent can want one without the other.
     */
    distanceWarningEnabled: z.boolean(),
    distanceWarning: z.object({
        /**
         * Nearer than this is too close. An estimate from a face in a camera, good
         * to a few centimetres, so the parent is shown "about" and never a reading.
         */
        tooCloseCentimetres: z
            .int()
            .min(DISTANCE_WARNING_CENTIMETRES.min)
            .max(DISTANCE_WARNING_CENTIMETRES.max),
    }),
});
//# sourceMappingURL=child.js.map